import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  DatePicker,
  InputNumber,
  message,
  Typography,
  Divider,
  Badge,
  Dropdown,
  Menu,
  Tooltip,
  Upload,
  Alert
} from 'antd';
import { useResponsive } from '../hooks/useResponsive';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ShoppingCartOutlined,
  DownOutlined,
  BarcodeOutlined,
  UploadOutlined,
  DownloadOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import ModernFormModal from '../components/ModernFormModal';
import { purchaseOrderService, purchaseOrderHelpers } from '../services/purchaseOrderService';
import { masterDataService, VendorProductLink } from '../services/masterDataService';
import { apiService } from '../services/apiService';
import { RBACService, PERMISSIONS } from '../utils/rbac';
import { downloadPurchaseOrderPdf } from '../utils/purchaseOrderPdf';
import QuickAddVendorProductModal from '../components/QuickAddVendorProductModal';

/** Purchase order dashboard summary cards — larger values (titles styled in App.css). */
const poSummaryValueStyle = (color: string): React.CSSProperties => ({
  color,
  fontWeight: 600,
  fontSize: 28,
  lineHeight: 1.2,
});

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Enums matching backend
enum PurchaseOrderStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED'
}

// Interfaces matching backend schema
interface PurchaseOrderItem {
  id?: number;
  po_id?: number;
  vendor_product_id?: number | null;
  product_id?: number | null;
  vendor_product?: {
    id: number;
    name: string;
    vendor_product_code?: string | null;
    unit_of_measure?: string;
  };
  product?: {
    id: number;
    name: string;
    code: string;
    selling_price?: number;
    cost_price?: number;
  };
  quantity: number;
  unit_price: number;
  total_price: number;
  received_quantity: number;
}

interface PurchaseOrder {
  id: number;
  po_number: string;
  vendor_id: number;
  vendor?: {
    id: number;
    name: string;
    email: string;
    phone: string;
  };
  location_id: number;
  location?: {
    id: number;
    name: string;
    code: string;
  };
  created_by: number;
  created_by_user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  order_date: string;
  expected_delivery_date?: string;
  status: PurchaseOrderStatus;
  total_amount: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  items: PurchaseOrderItem[];
}

type Vendor = { id: number; name: string };
type LocationLite = { id: number; name: string };

/** Prefer user's assigned location if listed; otherwise first accessible location. Never hardcode id 1. */
function defaultPurchaseOrderLocationId(locations: LocationLite[]): number | undefined {
  if (!locations.length) return undefined;
  const u = RBACService.getCurrentUser();
  const raw = u?.locationId ?? (u as any)?.location_id;
  if (raw != null && raw !== '') {
    const lid = Number(raw);
    if (Number.isFinite(lid) && locations.some((l) => l.id === lid)) {
      return lid;
    }
  }
  return locations[0].id;
}

const PurchaseOrders: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const canCreatePO = RBACService.hasPermission(PERMISSIONS.PO_CREATE);
  const canUpdatePO = RBACService.hasPermission(PERMISSIONS.PO_UPDATE);
  const canSendPO = RBACService.hasPermission(PERMISSIONS.PO_SEND);
  const canReceivePO = RBACService.hasPermission(PERMISSIONS.GOODS_RECEIVE);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PurchaseOrder | null>(null);
  const [viewingOrder, setViewingOrder] = useState<PurchaseOrder | null>(null);
  const [form] = Form.useForm();
  const vendorIdFormWatch = Form.useWatch('vendor_id', form);
  const [items, setItems] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | 'PARTIAL' | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [locations, setLocations] = useState<LocationLite[]>([]);
  const [vendorCatalogLinks, setVendorCatalogLinks] = useState<VendorProductLink[]>([]);
  const [quickAddCatalogOpen, setQuickAddCatalogOpen] = useState(false);
  const canManageVendorCatalog = RBACService.canWriteVendorCatalog();

  // Bulk PO from Excel
  const [bulkExcelModalVisible, setBulkExcelModalVisible] = useState(false);
  const [bulkExcelFile, setBulkExcelFile] = useState<File | null>(null);
  const [bulkExcelVendorId, setBulkExcelVendorId] = useState<number | undefined>();
  const [bulkExcelLocationId, setBulkExcelLocationId] = useState<number | undefined>();
  const [bulkExcelExpectedDate, setBulkExcelExpectedDate] = useState<string | undefined>();
  const [bulkExcelNotes, setBulkExcelNotes] = useState<string>('');
  const [bulkExcelResult, setBulkExcelResult] = useState<any>(null);
  const [bulkExcelUploading, setBulkExcelUploading] = useState(false);
  const [bulkExcelCreating, setBulkExcelCreating] = useState(false);
  
  // Load orders from backend (includes bulk-created POs from Excel); optional status filter
  const loadOrders = async () => {
    setLoading(true);
    try {
      // Fetch all POs (including bulk-created from Excel) for listing and stats
      const data = await purchaseOrderService.getPurchaseOrders({
        limit: 1000,
        status: undefined,
        location_id: undefined,
      });
      // Ensure items are properly mapped from order_items if needed
      const ordersWithItems = (data as any[]).map((order: any) => ({
        ...order,
        items: order.items || order.order_items || []
      }));
      setOrders(ordersWithItems as PurchaseOrder[]);
    } catch (e) {
      const isDebugMode = (process.env.REACT_APP_DEBUG || '').toLowerCase() === 'true';
      if (!isDebugMode) {
        message.error('Failed to load purchase orders');
      }
      console.error('Error loading purchase orders:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadVendorCatalog = useCallback(async (vendorId: number) => {
    if (!vendorId) {
      setVendorCatalogLinks([]);
      return;
    }
    try {
      const links = await masterDataService.getVendorProducts(vendorId);
      setVendorCatalogLinks(links || []);
    } catch {
      setVendorCatalogLinks([]);
    }
  }, []);

  useEffect(() => {
    loadOrders();
    (async () => {
      try {
        const [vList, lList] = await Promise.all([
          apiService.get<Vendor[]>('/vendors/'),
          masterDataService.getLocations
            ? masterDataService.getLocations({ active_only: true, forceRefresh: true })
            : Promise.resolve([] as any),
        ]);
        setVendors(vList || []);
        setLocations((lList as any) || []);
      } catch {
        // Suppress errors in debug mode
        const isDebugMode = (process.env.REACT_APP_DEBUG || '').toLowerCase() === 'true';
        if (!isDebugMode) {
          message.error('Failed to load master data');
        }
      }
    })();
  }, []);

  // Calculate statistics
  const stats = {
    total_orders: orders.length,
    draft_orders: orders.filter(o => o.status === PurchaseOrderStatus.DRAFT).length,
    sent_orders: orders.filter(o => o.status === PurchaseOrderStatus.SENT && !purchaseOrderHelpers.isPartiallyReceived(o)).length,
    partial_orders: orders.filter(o => purchaseOrderHelpers.isPartiallyReceived(o)).length,
    received_orders: orders.filter(o => o.status === PurchaseOrderStatus.RECEIVED).length,
    // Total Value: Only count SENT orders (orders that have been sent to vendors)
    total_amount: orders
      .filter(o => o.status === PurchaseOrderStatus.SENT)
      .reduce((sum, o) => {
        const amount = typeof o.total_amount === 'number' ? o.total_amount : parseFloat(o.total_amount) || 0;
        return sum + amount;
      }, 0),
    pending_amount: orders
      .filter(o => o.status === PurchaseOrderStatus.SENT)
      .reduce((sum, o) => {
        const amount = typeof o.total_amount === 'number' ? o.total_amount : parseFloat(o.total_amount) || 0;
        return sum + amount;
      }, 0)
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchText ||
      order.po_number.toLowerCase().includes(searchText.toLowerCase()) ||
      order.vendor?.name.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = !statusFilter
      || (statusFilter === 'PARTIAL'
        ? purchaseOrderHelpers.isPartiallyReceived(order)
        : statusFilter === PurchaseOrderStatus.SENT
          ? order.status === PurchaseOrderStatus.SENT && !purchaseOrderHelpers.isPartiallyReceived(order)
          : order.status === statusFilter);
    return matchesSearch && matchesStatus;
  });

  const handleCreate = () => {
    setEditingOrder(null);
    form.resetFields();
    form.setFieldsValue({
      order_date: moment(),
      location_id: defaultPurchaseOrderLocationId(locations),
    });
    setItems([]);
    setVendorCatalogLinks([]);
    setModalVisible(true);
  };

  // If locations load after the create modal opens, fill a real location (avoid leftover "1").
  useEffect(() => {
    if (!modalVisible || editingOrder || locations.length === 0) return;
    const current = form.getFieldValue('location_id');
    const valid = locations.some((l) => l.id === current);
    if (!valid) {
      form.setFieldsValue({ location_id: defaultPurchaseOrderLocationId(locations) });
    }
  }, [modalVisible, editingOrder, locations, form]);

  const handleEdit = async (order: PurchaseOrder) => {
    const hideLoading = message.loading('Loading purchase order…', 0);
    try {
      const full = (await purchaseOrderService.getPurchaseOrder(order.id!)) as PurchaseOrder & {
        order_items?: PurchaseOrder['items'];
      };
      const lineItems =
        (Array.isArray(full.items) && full.items.length > 0 && full.items) ||
        (Array.isArray(full.order_items) && full.order_items.length > 0 && full.order_items) ||
        [];

      setEditingOrder(full);
      form.setFieldsValue({
        vendor_id: full.vendor_id,
        location_id: full.location_id,
        order_date: moment(full.order_date),
        expected_delivery_date: full.expected_delivery_date ? moment(full.expected_delivery_date) : null,
        notes:
          full.notes && !String(full.notes).startsWith('Created from Excel file:')
            ? full.notes
            : '',
      });
      setItems(
        lineItems.map((item: any, idx: number) => {
          const qty = Number(item.quantity) || 0;
          const up = Number(item.unit_price) || 0;
          return {
            key: `item-${item.id ?? idx}-${idx}`,
            vendor_product_id: item.vendor_product_id,
            quantity: qty,
            unit_price: up,
            total_price:
              Number(item.total_price) || Math.round(qty * up * 100) / 100,
          };
        })
      );
      await loadVendorCatalog(full.vendor_id);
      setModalVisible(true);
    } catch (e) {
      console.error('Error loading PO for edit:', e);
      message.error('Failed to load purchase order for editing');
    } finally {
      hideLoading();
    }
  };

  const handleView = async (order: PurchaseOrder) => {
    try {
      // Fetch full order details with items from backend
      const fullOrder = await purchaseOrderService.getPurchaseOrder(order.id);
      setViewingOrder(fullOrder as PurchaseOrder);
      setDetailsModalVisible(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      // Fallback to using the order from the list if API call fails
      setViewingOrder(order);
      setDetailsModalVisible(true);
    }
  };

  const handleDelete = (orderId: number) => {
    setOrders(orders.filter(o => o.id !== orderId));
    message.success('✅ Purchase order deleted successfully');
  };

  const handleStatusChange = async (orderId: number, newStatus: PurchaseOrderStatus) => {
    setLoading(true);
    try {
      let updatedOrder: any;
      
      // If sending to vendor, use the send email endpoint
      if (newStatus === PurchaseOrderStatus.SENT) {
        const result = await purchaseOrderService.sendPurchaseOrderEmail(orderId);
        updatedOrder = { ...orders.find(o => o.id === orderId)!, status: PurchaseOrderStatus.SENT };
        message.success(`✅ Order sent to vendor successfully`);
        if (result.email_sent) {
          message.info('📧 Email notification sent to vendor');
        }
      } else {
        // For other status changes, use the status update endpoint
        const result = await purchaseOrderService.updatePurchaseOrderStatus(orderId, newStatus);
        updatedOrder = { ...orders.find(o => o.id === orderId)!, status: newStatus as PurchaseOrderStatus };
        message.success(`✅ Order status updated to ${newStatus}`);
        
        if (newStatus === PurchaseOrderStatus.RECEIVED && result.goods_received_created) {
          message.info('📦 Goods received record created automatically');
        }
      }
      
      // Update local state
      setOrders(orders.map(o => o.id === orderId ? updatedOrder : o));
      
      // Reload orders to ensure we have the latest data from backend
      await loadOrders();
      
    } catch (error: any) {
      console.error('Error updating order status:', error);
      message.error(error?.message || `Failed to update order status to ${newStatus}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (items.length === 0) {
      message.error('Please add at least one item');
      return;
    }
    const isDraftEdit = editingOrder?.status === PurchaseOrderStatus.DRAFT;
    const catalogRequiredOnEachRow = !editingOrder || isDraftEdit;
    if (catalogRequiredOnEachRow && items.some((it: any) => !it.vendor_product_id)) {
      message.error('Select a vendor catalog line on every row');
      return;
    }
    if (!values.location_id) {
      message.error('Please select a location');
      return;
    }
    if (items.some((it: any) => !(Number(it.unit_price) > 0))) {
      message.error('Enter a unit price greater than 0 for every row (vendor catalog price is 0 — type the cost)');
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        vendor_id: values.vendor_id,
        location_id: values.location_id,
        order_date: values.order_date.format('YYYY-MM-DD'),
        expected_delivery_date: values.expected_delivery_date?.format('YYYY-MM-DD'),
        notes: values.notes,
        items: items.map((it: any) => ({
          vendor_product_id: it.vendor_product_id,
          quantity: it.quantity,
          unit_price: it.unit_price
        }))
      };

      if (editingOrder && !isDraftEdit) {
        delete payload.items;
      }

      if (editingOrder) {
        const updated = await purchaseOrderService.updatePurchaseOrder(editingOrder.id, payload);
        setOrders(orders.map(o => (o.id === editingOrder.id ? (updated as any) : o)));
        message.success('Purchase order updated successfully');
      } else {
        const created = await purchaseOrderService.createPurchaseOrder(payload);
        setOrders([created as any, ...orders]);
        message.success('Purchase order created successfully');
      }
      setModalVisible(false);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ')
            : e?.message || 'Failed to save purchase order';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        key: `item-${Date.now()}`,
        vendor_product_id: null as any,
        quantity: 1,
        unit_price: 0,
        total_price: 0
      }
    ]);
  };

  const removeItem = (key: string) => {
    setItems(items.filter(item => item.key !== key));
  };

  const updateItem = (key: string, field: string, value: any) => {
    setItems(items.map(item => {
      if (item.key === key) {
        const updated = { ...item, [field]: value };
        
        if (field === 'vendor_product_id') {
          const line = vendorCatalogLinks.find((l) => l.id === value);
          if (line) {
            const fromVendor = Number(line.vendor_price);
            const fromProduct = Number((line as any).product?.cost_price ?? (line as any).product?.unit_price ?? 0);
            const price = fromVendor > 0 ? fromVendor : fromProduct > 0 ? fromProduct : 0;
            updated.unit_price = price;
          }
        }

        if (field === 'quantity' || field === 'unit_price' || field === 'vendor_product_id') {
          const qty = Number(updated.quantity) || 0;
          const price = Number(updated.unit_price) || 0;
          updated.total_price = Math.round(qty * price * 100) / 100;
        }
        
        return updated;
      }
      return item;
    }));
  };

  const handleBulkParse = async () => {
    if (!bulkExcelFile || !bulkExcelVendorId || !bulkExcelLocationId) return;
    setBulkExcelUploading(true);
    setBulkExcelResult(null);
    try {
      const res = await purchaseOrderService.uploadPoExcel(
        bulkExcelFile,
        bulkExcelVendorId,
        bulkExcelLocationId,
        bulkExcelExpectedDate || undefined,
        bulkExcelNotes || undefined
      );
      setBulkExcelResult(res);
      if (res.success) {
        message.success(`Parsed: ${res.valid_items?.length ?? 0} valid, ${res.invalid_items?.length ?? 0} invalid`);
      } else {
        const invalidCount = res.invalid_items?.length ?? 0;
        message.warning(`No valid rows found (${invalidCount} invalid). Check quantity, price, and column names.`);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBulkExcelUploading(false);
    }
  };

  const getActionMenu = (record: PurchaseOrder) => (
    <Menu
      onClick={({ key, domEvent }) => {
        domEvent?.stopPropagation();
        if (key === 'view') handleView(record);
        else if (key === 'edit') handleEdit(record);
        else if (key === 'send') handleStatusChange(record.id, PurchaseOrderStatus.SENT);
        else if (key === 'receive') navigate(`/grpo?po_id=${record.id}`);
        else if (key === 'cancel') handleStatusChange(record.id, PurchaseOrderStatus.CANCELLED);
        else if (key === 'delete') {
          Modal.confirm({
            title: 'Delete Purchase Order',
            content: `Are you sure you want to delete ${record.po_number}?`,
            okText: 'Delete',
            okType: 'danger',
            onOk: () => handleDelete(record.id),
          });
        }
      }}
    >
      <Menu.Item key="view" icon={<EyeOutlined />}>
        View
      </Menu.Item>
      {canUpdatePO && record.status === PurchaseOrderStatus.DRAFT && (
        <Menu.Item key="edit" icon={<EditOutlined />}>
          Edit
        </Menu.Item>
      )}
      {canSendPO && record.status === PurchaseOrderStatus.DRAFT && (
        <Menu.Item key="send" icon={<SendOutlined />}>
          Send to Vendor
        </Menu.Item>
      )}
      {canReceivePO && record.status === PurchaseOrderStatus.SENT && (
        <Menu.Item key="receive" icon={<CheckCircleOutlined />}>
          Create GRPO
        </Menu.Item>
      )}
      {canUpdatePO && record.status !== PurchaseOrderStatus.CANCELLED && record.status !== PurchaseOrderStatus.RECEIVED && (
        <Menu.Item key="cancel" icon={<CloseCircleOutlined />} danger>
          Cancel Order
        </Menu.Item>
      )}
      {canUpdatePO && record.status === PurchaseOrderStatus.DRAFT && (
        <>
          <Menu.Divider />
          <Menu.Item key="delete" icon={<DeleteOutlined />} danger>
            Delete
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const columns: ColumnsType<PurchaseOrder> = [
    {
      title: 'PO Number',
      dataIndex: 'po_number',
      key: 'po_number',
      width: 140,
      render: (text: string, record: PurchaseOrder) => (
        <Text
          style={{
            color: '#1890ff',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '12px',
            fontWeight: 400
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleView(record);
          }}
        >
          {text}
        </Text>
      )
    },
    {
      title: 'Vendor',
      key: 'vendor',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const v = record.vendor || vendors.find(vv => vv.id === record.vendor_id);
        return (
          <div style={{ fontSize: '12px', fontWeight: 400 }}>{v?.name || '-'}</div>
        );
      }
    },
    {
      title: 'Location',
      key: 'location',
      width: 240,
      ellipsis: true,
      render: (_, record) => {
        const loc = record.location || locations.find(ll => ll.id === record.location_id);
        const name = loc?.name || '-';
        return (
          <Tag className="po-location-tag" title={name}>
            {name}
          </Tag>
        );
      }
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      width: 120,
      render: (date: string) => moment(date).format('DD MMM YYYY')
    },
    {
      title: 'Expected Delivery',
      dataIndex: 'expected_delivery_date',
      key: 'expected_delivery_date',
      width: 140,
      render: (date?: string) => date ? moment(date).format('DD MMM YYYY') : '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (_status: PurchaseOrderStatus, record) => {
        const label = purchaseOrderHelpers.displayStatus(record);
        return <Tag color={label.color}>{label.text}</Tag>;
      }
    },
    {
      title: 'Items',
      key: 'items',
      width: 100,
      align: 'center',
      render: (_, record) => {
        // Handle both 'items' and 'order_items' from backend
        const items = record.items || (record as any).order_items || [];
        const itemCount = Array.isArray(items) ? items.length : 0;
        return (
          <Badge count={itemCount} showZero style={{ backgroundColor: '#52c41a' }} />
        );
      }
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 140,
      align: 'right',
      render: (amount: number) => (
        <Text style={{ color: '#52c41a', fontSize: '12px', fontWeight: 400 }}>
          Nu. {amount.toLocaleString()}
        </Text>
      )
    },
    {
      title: 'Still to receive',
      key: 'progress',
      width: 160,
      render: (_, record) => {
        const { received, remaining } = purchaseOrderHelpers.quantities(record);
        if (record.status === PurchaseOrderStatus.DRAFT || record.status === PurchaseOrderStatus.CANCELLED) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <div>
            <div>{received} received</div>
            <Text type={remaining > 0 ? 'warning' : 'secondary'}>
              {remaining > 0 ? `${remaining} still to receive` : 'All received'}
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Operations',
      key: 'operations',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Dropdown overlay={getActionMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="primary"
            ghost
            size="small"
            className="po-action-manage"
            aria-label="Manage purchase order row"
            onClick={(e) => e.stopPropagation()}
          >
            Manage <DownOutlined />
          </Button>
        </Dropdown>
      )
    }
  ];

  const itemColumns: ColumnsType<any> = [
    {
      title: 'Vendor product',
      key: 'vendor_product',
      width: 260,
      render: (_, record) => (
        <Select
          style={{ width: '100%' }}
          placeholder={vendorCatalogLinks.length ? 'Select vendor line' : 'Add vendor catalog first'}
          value={record.vendor_product_id || undefined}
          onChange={(value) => updateItem(record.key, 'vendor_product_id', value)}
          showSearch
          optionFilterProp="children"
          className="centered-select-arrow"
          disabled={!vendorCatalogLinks.length}
        >
          {vendorCatalogLinks.map((line) => (
            <Option key={line.id} value={line.id}>
              {line.name}
              {line.vendor_product_code ? ` (${line.vendor_product_code})` : ''}
              {line.product ? ` → ${line.product.code}` : ''}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Quantity',
      key: 'quantity',
      width: 100,
      render: (_, record) => (
        <InputNumber
          min={1}
          value={record.quantity}
          onChange={(value) => updateItem(record.key, 'quantity', value || 1)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Unit Price',
      key: 'unit_price',
      width: 120,
      render: (_, record) => (
        <InputNumber
          min={0}
          precision={2}
          value={record.unit_price}
          onChange={(value) => updateItem(record.key, 'unit_price', value || 0)}
          style={{ width: '100%' }}
          formatter={(val) => `Nu. ${val}`}
          parser={(val) => parseFloat(val?.replace(/Nu\.\s?|(,*)/g, '') || '0')}
        />
      )
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 120,
      render: (value: number) => (
        <Text style={{ fontSize: '12px', fontWeight: 400 }}>Nu. {(value || 0).toLocaleString()}</Text>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 72,
      align: 'center',
      fixed: isMobile ? undefined : 'right',
      render: (_, record) => (
        <Tooltip title="Remove item">
          <Button
            type="text"
            onClick={() => removeItem(record.key)}
            icon={<DeleteOutlined />}
            className="po-item-remove-btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 32,
              height: 32,
              padding: '0 8px',
              color: 'rgba(0,0,0,0.45)',
            }}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div className="purchase-orders-page" style={{ minHeight: '100%', overflowY: 'auto', position: 'relative' }}>
      {/* Header with Search in Top Right */}
      <Row gutter={[16, 16]} justify="space-between" align="middle" style={{ marginBottom: 24, position: 'relative', zIndex: 100 }}>
        <Col xs={24} sm={24} md={12} lg={10}>
          <Title level={2} style={{ margin: 0, fontSize: '14px', fontWeight: 400 }}>
            <ShoppingCartOutlined /> Purchase Orders
          </Title>
          <Text type="secondary" style={{ fontSize: '12px', fontWeight: 400 }}>Manage purchase orders and track deliveries</Text>
        </Col>
        <Col xs={24} sm={24} md={12} lg={14}>
          <Space 
            wrap 
            style={{ width: '100%', justifyContent: isMobile ? 'stretch' : 'flex-end' }}
            direction={isMobile ? 'vertical' : 'horizontal'}
            size="middle"
          >
            <Input.Search
              className="app-search"
              placeholder="Search by PO number or vendor..."
              allowClear
              prefix={null}
              value={searchText}
              onSearch={(value) => setSearchText(value)}
              onChange={(e) => setSearchText(e.target.value)}
              enterButton
              size="large"
              style={{ width: isMobile ? '100%' : 250 }}
            />
            <Select
              placeholder="All Status"
              allowClear
              style={{ width: isMobile ? '100%' : 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
              size="large"
              suffixIcon={null}
            >
              {Object.values(PurchaseOrderStatus).map(status => (
                <Option key={status} value={status}>
                  {purchaseOrderHelpers.getStatusText(status)}
                </Option>
              ))}
              <Option value="PARTIAL">Partially received</Option>
            </Select>
            {canCreatePO && (
              <>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                  size="large"
                  block={isMobile}
                >
                  New Order
                </Button>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => {
                    setBulkExcelModalVisible(true);
                    setBulkExcelResult(null);
                    setBulkExcelFile(null);
                    setBulkExcelVendorId(undefined);
                    setBulkExcelLocationId(locations[0]?.id);
                    setBulkExcelExpectedDate(undefined);
                    setBulkExcelNotes('');
                  }}
                  size="large"
                  block={isMobile}
                >
                  Bulk (Excel)
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards - same style as Inventory */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }} className="purchase-orders-summary-row">
        <Col xs={24} sm={12} lg={6}>
          <Card 
            className="page-stat-card page-stat-card--blue"
            hoverable
            onClick={() => setStatusFilter(null)}
            style={{ cursor: 'pointer', border: !statusFilter ? '2px solid #1890ff' : undefined }}
          >
            <Statistic
              title="Total Orders"
              value={stats.total_orders}
              prefix={<ShoppingCartOutlined />}
              valueStyle={poSummaryValueStyle('#1890ff')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            className="page-stat-card page-stat-card--amber"
            hoverable
            onClick={() => setStatusFilter(PurchaseOrderStatus.DRAFT)}
            style={{ cursor: 'pointer', border: statusFilter === PurchaseOrderStatus.DRAFT ? '2px solid #faad14' : undefined }}
          >
            <Statistic
              title="Draft"
              value={stats.draft_orders}
              prefix={<EditOutlined />}
              valueStyle={poSummaryValueStyle('#faad14')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            className="page-stat-card page-stat-card--green"
            hoverable
            onClick={() => setStatusFilter(PurchaseOrderStatus.SENT)}
            style={{ cursor: 'pointer', border: statusFilter === PurchaseOrderStatus.SENT ? '2px solid #52c41a' : undefined }}
          >
            <Statistic
              title="Pending"
              value={stats.sent_orders}
              prefix={<SendOutlined />}
              valueStyle={poSummaryValueStyle('#52c41a')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            className="page-stat-card page-stat-card--amber"
            hoverable
            onClick={() => setStatusFilter('PARTIAL')}
            style={{ cursor: 'pointer', border: statusFilter === 'PARTIAL' ? '2px solid #fa8c16' : undefined }}
          >
            <Statistic
              title="Partially received"
              value={stats.partial_orders}
              prefix={<CheckCircleOutlined />}
              valueStyle={poSummaryValueStyle('#fa8c16')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="page-stat-card page-stat-card--violet">
            <Statistic
              title="Total Value (Sent)"
              value={stats.total_amount}
              valueStyle={poSummaryValueStyle('#722ed1')}
              formatter={(value) => {
                const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;
                return `Nu. ${numValue.toLocaleString()}`;
              }}
            />
          </Card>
        </Col>
      </Row>


      {/* Orders Table */}
      <Card 
        className="page-table-card page-table-card--blue purchase-orders-table-card"
        bodyStyle={{ 
          overflow: 'visible',
          padding: 0,
          position: 'relative',
          minHeight: isMobile ? '300px' : '400px',
          display: 'flex',
          flexDirection: 'column',
          border: 'none'
        }}
        style={{ 
          position: 'relative',
          zIndex: 1,
          minHeight: isMobile ? '300px' : '400px',
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
          boxShadow: 'none'
        }}
      >
        {filteredOrders.length === 0 && !loading ? (
          <div className="empty-state" style={{ padding: '60px 20px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">No purchase orders found</div>
            <div className="empty-state-description">
              {searchText || statusFilter 
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by creating your first purchase order'}
            </div>
            {canCreatePO && !searchText && !statusFilter && (
              <Space size="middle" style={{ marginTop: 16 }}>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleCreate}
                  size="large"
                >
                  Create PO (manual)
                </Button>
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => {
                    setBulkExcelModalVisible(true);
                    setBulkExcelResult(null);
                    setBulkExcelFile(null);
                    setBulkExcelVendorId(undefined);
                    setBulkExcelLocationId(locations[0]?.id);
                    setBulkExcelExpectedDate(undefined);
                    setBulkExcelNotes('');
                  }}
                  size="large"
                >
                  Bulk (Excel)
                </Button>
              </Space>
            )}
          </div>
        ) : (
          <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Table
          columns={columns}
          dataSource={filteredOrders}
          rowKey="id"
          loading={loading}
              scroll={{ 
                x: isMobile ? 1400 : 1600
              }}
          pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                showSizeChanger: !isMobile,
            pageSizeOptions: ['5', '10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
                responsive: true,
                position: ['bottomCenter']
          }}
              size={isMobile ? 'small' : 'middle'}
        />
          </div>
        )}
      </Card>

      {/* Create/Edit Modal - same header as PO Details (light gray bar, optional summary), form body scrolls */}
      <ModernFormModal
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        title={editingOrder ? `Purchase Order Details - ${editingOrder.po_number}` : 'Create New Purchase Order'}
        subtitle={editingOrder ? 'Update the purchase order details below.' : 'Fill in the details below to create a new purchase order.'}
        headerVariant="light"
        headerSummary={editingOrder ? (
          <>
            <div><span className="mf-summary-label">PO Number:</span> <span className="mf-summary-value">{editingOrder.po_number}</span></div>
            <div><span className="mf-summary-label">Order Date:</span> <span className="mf-summary-value">{editingOrder.order_date ? purchaseOrderHelpers.formatDate(editingOrder.order_date) : '—'}</span></div>
            <div><span className="mf-summary-label">Vendor:</span> <span className="mf-summary-value">{editingOrder.vendor?.name ?? '—'}</span></div>
            <div><span className="mf-summary-label">Expected Delivery:</span> <span className="mf-summary-value">{editingOrder.expected_delivery_date ? purchaseOrderHelpers.formatDate(editingOrder.expected_delivery_date) : '—'}</span></div>
            <div><span className="mf-summary-label">Location:</span> <span className="mf-summary-value">{editingOrder.location?.name ?? '—'}</span></div>
            <div><span className="mf-summary-label">Status:</span> <Tag color={purchaseOrderHelpers.getStatusColor(editingOrder.status)}>{purchaseOrderHelpers.getStatusText(editingOrder.status)}</Tag></div>
          </>
        ) : null}
        getContainer={() => document.querySelector('.main-content-full') || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Cancel"
        okText={editingOrder ? 'Update Order' : 'Create Order'}
        onOk={() => form.submit()}
        isSubmitting={loading}
        destroyOnClose={false}
      >
        <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="vendor_id"
                  label="Vendor"
                  rules={[{ required: true, message: 'Please select vendor' }]}
                >
                  <Select
                    placeholder="Select vendor"
                    showSearch
                    optionFilterProp="children"
                    className="centered-select-arrow"
                    onChange={(vid: number) => {
                      loadVendorCatalog(vid);
                      setItems([]);
                    }}
                  >
                    {vendors.map(vendor => (
                      <Option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="location_id"
                  label="Location"
                  rules={[{ required: true, message: 'Please select location' }]}
                >
                  <Select 
                    placeholder="Select location"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    className="centered-select-arrow"
                  >
                    {locations.map(location => (
                      <Option key={location.id} value={location.id}>
                        {location.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="order_date"
                  label="Order Date"
                  rules={[{ required: true, message: 'Please select order date' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="expected_delivery_date" label="Expected Delivery Date">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="notes" label="Notes">
              <TextArea rows={2} placeholder="Add any additional notes..." autoComplete="off" />
            </Form.Item>

            <Divider orientation="left">Order Items</Divider>

            {vendorIdFormWatch && vendorCatalogLinks.length === 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="No vendor catalog lines yet"
                description={
                  canManageVendorCatalog
                    ? 'Use “Add new vendor product” to add what you buy from this supplier (saved to Vendor products), then add PO rows.'
                    : 'Ask an admin to add vendor lines under Vendor products, or grant you vendor catalog permission to add lines from here.'
                }
              />
            )}

            <Space wrap style={{ marginBottom: 16, width: '100%' }}>
              <Tooltip
                title={
                  !vendorIdFormWatch
                    ? 'Select a vendor first'
                    : !canManageVendorCatalog
                      ? 'Missing permission: vendor product manage'
                      : 'Open vendor product form for this supplier'
                }
              >
                <Button
                  type="default"
                  icon={<ShopOutlined />}
                  disabled={!vendorIdFormWatch || !canManageVendorCatalog}
                  onClick={() => setQuickAddCatalogOpen(true)}
                >
                  Add new vendor product
                </Button>
              </Tooltip>
              <Button
                type="dashed"
                onClick={addItem}
                icon={<PlusOutlined />}
                disabled={!vendorIdFormWatch}
              >
                Add PO row
              </Button>
            </Space>

            <Table
              columns={itemColumns}
              dataSource={items}
              pagination={false}
              size="small"
              scroll={{ x: 1000 }}
              style={{ width: '100%' }}
              locale={{ emptyText: 'No items added yet. Click "Add Item" to get started.' }}
            />

            <Card style={{ marginTop: 16, backgroundColor: '#f0f2f5' }}>
              <Row justify="end">
                <Col>
                  <Title level={4} style={{ margin: 0, fontSize: '12px', fontWeight: 400 }}>
                    Total Amount: Nu. {items.reduce((sum, item) => sum + (Number(item.total_price) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Title>
                </Col>
              </Row>
            </Card>
          </Form>

          <QuickAddVendorProductModal
            open={quickAddCatalogOpen}
            vendorId={vendorIdFormWatch}
            vendorName={vendors.find((v) => v.id === vendorIdFormWatch)?.name}
            getContainer={() => document.querySelector('.main-content-full') || document.body}
            wrapClassName="form-panel-full-wrap"
            onClose={() => setQuickAddCatalogOpen(false)}
            onCreated={(line) => {
              if (vendorIdFormWatch) loadVendorCatalog(vendorIdFormWatch);
              const price = Number(line.vendor_price) > 0 ? Number(line.vendor_price) : 0;
              setItems((prev) => [
                ...prev,
                {
                  key: `item-${Date.now()}`,
                  vendor_product_id: line.id,
                  quantity: 1,
                  unit_price: price,
                  total_price: price,
                },
              ]);
            }}
          />
        </div>
      </ModernFormModal>

      {/* Bulk PO from Excel Modal - same header as Add New PO (light blue) */}
      <ModernFormModal
        open={bulkExcelModalVisible}
        onClose={() => setBulkExcelModalVisible(false)}
        title="Bulk Create PO from Excel"
        subtitle="Upload an Excel file and select vendor and location. Columns: Product Code, Product Name, Quantity, Unit Price. Items that are not in your products, or not on this vendor, are added when you create the PO."
        headerVariant="light"
        getContainer={() => document.querySelector('.main-content-full') || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Close"
        okText="Parse file"
        onOk={handleBulkParse}
        isSubmitting={bulkExcelUploading}
        okDisabled={!bulkExcelFile || !bulkExcelVendorId || !bulkExcelLocationId}
        destroyOnClose
      >
        <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Form layout="vertical" style={{ width: '100%' }}>
              <Form.Item label="Vendor" required>
                <Select
                  placeholder="Select vendor"
                  value={bulkExcelVendorId}
                  onChange={setBulkExcelVendorId}
                  style={{ width: '100%' }}
                  options={vendors.map(v => ({ value: v.id, label: v.name }))}
                />
              </Form.Item>
              <Form.Item label="Location" required>
                <Select
                  placeholder="Select location"
                  value={bulkExcelLocationId}
                  onChange={setBulkExcelLocationId}
                  style={{ width: '100%' }}
                  options={locations.map(l => ({ value: l.id, label: l.name }))}
                />
              </Form.Item>
              <Form.Item label="Expected delivery date (optional)">
                <DatePicker
                  style={{ width: '100%' }}
                  value={bulkExcelExpectedDate ? moment(bulkExcelExpectedDate) : null}
                  onChange={(_, dateStr) => setBulkExcelExpectedDate(dateStr || undefined)}
                />
              </Form.Item>
              <Form.Item label="Notes (optional)">
                <TextArea
                  rows={2}
                  value={bulkExcelNotes}
                  onChange={(e) => setBulkExcelNotes(e.target.value)}
                  placeholder="PO notes..."
                />
              </Form.Item>
            </Form>
            <Upload
              accept=".xlsx,.xls"
              maxCount={1}
              beforeUpload={(file) => { setBulkExcelFile(file); setBulkExcelResult(null); return false; }}
              onRemove={() => { setBulkExcelFile(null); setBulkExcelResult(null); }}
            >
              <Button icon={<UploadOutlined />}>Select Excel file</Button>
            </Upload>
            <Button
              type="primary"
              disabled={!bulkExcelFile || !bulkExcelVendorId || !bulkExcelLocationId}
              loading={bulkExcelUploading}
              onClick={handleBulkParse}
            >
              Parse file
            </Button>
          {bulkExcelResult && (bulkExcelResult.invalid_items?.length ?? 0) > 0 && (
            <Alert
              message={`${bulkExcelResult.invalid_items?.length ?? 0} row(s) invalid`}
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {(bulkExcelResult.invalid_items ?? [])
                    .slice(0, 5)
                    .map((item: any, i: number) => (
                      <li key={i}>
                        Row {item.row_number}: {item.error || 'Invalid'}
                        {item.product_code && ` (Code: ${item.product_code})`}
                      </li>
                    ))}
                  {(bulkExcelResult.invalid_items?.length ?? 0) > 5 && (
                    <li>… and {(bulkExcelResult.invalid_items?.length ?? 0) - 5} more</li>
                  )}
                </ul>
              }
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
          {bulkExcelResult?.suggested_po_data && (
            <div style={{ marginTop: 16 }}>
              <Alert
                message={`Valid items: ${bulkExcelResult.valid_items?.length ?? 0} — ready to create one PO`}
                description={
                  (bulkExcelResult.valid_items ?? []).some((item: { note?: string }) => item.note) ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {(bulkExcelResult.valid_items ?? [])
                        .filter((item: { note?: string }) => item.note)
                        .map((item: { row_number: number; product_code?: string; product_name?: string; note?: string }) => (
                          <li key={item.row_number}>
                            Row {item.row_number}
                            {item.product_code ? ` (${item.product_code})` : ''}
                            {item.product_name ? ` ${item.product_name}` : ''}: {item.note}
                          </li>
                        ))}
                    </ul>
                  ) : undefined
                }
                type="success"
                showIcon
                style={{ marginBottom: 12 }}
              />
              <Button
                type="primary"
                loading={bulkExcelCreating}
                onClick={async () => {
                  setBulkExcelCreating(true);
                  try {
                    await purchaseOrderService.createPoFromExcel(bulkExcelResult.suggested_po_data);
                    message.success('Purchase order created from Excel.');
                    setBulkExcelModalVisible(false);
                    loadOrders();
                  } catch (e: any) {
                    message.error(e?.response?.data?.detail || 'Create failed');
                  } finally {
                    setBulkExcelCreating(false);
                  }
                }}
              >
                Create PO
              </Button>
            </div>
          )}
          </Space>
        </div>
      </ModernFormModal>

      {/* View PO Details Modal - same header as Add New PO (light blue) */}
      <ModernFormModal
        open={detailsModalVisible}
        onClose={() => setDetailsModalVisible(false)}
        title={`Purchase Order Details - ${viewingOrder?.po_number ?? ''}`}
        subtitle="View and manage this purchase order."
        headerVariant="light"
        headerSummary={viewingOrder ? (
          <>
            <div><span className="mf-summary-label">PO Number:</span> <span className="mf-summary-value">{viewingOrder.po_number}</span></div>
            <div><span className="mf-summary-label">Order Date:</span> <span className="mf-summary-value">{viewingOrder.order_date ? moment(viewingOrder.order_date).format('DD MMM YYYY') : '—'}</span></div>
            <div><span className="mf-summary-label">Vendor:</span> <span className="mf-summary-value">{viewingOrder.vendor?.name ?? '—'}</span></div>
            <div><span className="mf-summary-label">Expected Delivery:</span> <span className="mf-summary-value">{viewingOrder.expected_delivery_date ? moment(viewingOrder.expected_delivery_date).format('DD MMM YYYY') : '—'}</span></div>
            <div><span className="mf-summary-label">Location:</span> <span className="mf-summary-value">{viewingOrder.location?.name ?? '—'}</span></div>
            <div><span className="mf-summary-label">Status:</span> <Tag color={purchaseOrderHelpers.displayStatus(viewingOrder).color}>{purchaseOrderHelpers.displayStatus(viewingOrder).text}</Tag></div>
          </>
        ) : null}
        getContainer={() => document.querySelector('.main-content-full') || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Close"
        okText="Download PDF"
        okIcon={<DownloadOutlined />}
        onOk={async () => {
          if (!viewingOrder) return;
          let logoDataUrl: string | null = null;
          try {
            const blob = await apiService.download('/auth/me/company-logo');
            logoDataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch {
            // No logo or fetch failed – PDF will render without logo
          }
          await downloadPurchaseOrderPdf(viewingOrder, { logoDataUrl });
        }}
      >
        {viewingOrder && (
          <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
            <Row gutter={16}>
              <Col span={12}>
                <p style={{ fontSize: '12px', fontWeight: 400 }}><span style={{ fontWeight: 400 }}>PO Number:</span> {viewingOrder.po_number}</p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}><span style={{ fontWeight: 400 }}>Vendor:</span> {viewingOrder.vendor?.name}</p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}><span style={{ fontWeight: 400 }}>Location:</span> {viewingOrder.location?.name}</p>
              </Col>
              <Col span={12}>
                <p style={{ fontSize: '12px', fontWeight: 400 }}><span style={{ fontWeight: 400 }}>Order Date:</span> {moment(viewingOrder.order_date).format('DD MMM YYYY')}</p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}><span style={{ fontWeight: 400 }}>Expected Delivery:</span> {viewingOrder.expected_delivery_date ? moment(viewingOrder.expected_delivery_date).format('DD MMM YYYY') : '-'}</p>
                <p style={{ fontSize: '12px', fontWeight: 400 }}><span style={{ fontWeight: 400 }}>Status:</span> <Tag color={purchaseOrderHelpers.displayStatus(viewingOrder).color}>{purchaseOrderHelpers.displayStatus(viewingOrder).text}</Tag></p>
              </Col>
            </Row>

            <Divider />

            {(viewingOrder.items && viewingOrder.items.length > 0) || ((viewingOrder as any).order_items && (viewingOrder as any).order_items.length > 0) ? (
              <>
                <Title level={5} style={{ fontSize: '12px', fontWeight: 400 }}>Order Items</Title>
                <Table
                  dataSource={viewingOrder.items || (viewingOrder as any).order_items || []}
                  rowKey={(record, index) => record.id || `item-${index}`}
                  pagination={false}
                  size="small"
                  columns={[
                {
                  title: 'Vendor product',
                  key: 'vendor_product',
                  render: (_, record) => (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 400 }}>
                        {record.vendor_product?.name || record.product?.name || '—'}
                      </div>
                      <Text type="secondary" style={{ fontSize: '12px', fontWeight: 400 }}>
                        <BarcodeOutlined />{' '}
                        {record.vendor_product?.vendor_product_code || record.product?.code || '—'}
                        {record.product?.code && record.vendor_product?.name ? ` · ${record.product.code}` : ''}
                      </Text>
                    </div>
                  )
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  width: 100,
                  align: 'center'
                },
                {
                  title: 'Unit Price',
                  dataIndex: 'unit_price',
                  key: 'unit_price',
                  width: 120,
                  align: 'right',
                  render: (price: number) => `Nu. ${price.toLocaleString()}`
                },
                {
                  title: 'Total',
                  dataIndex: 'total_price',
                  key: 'total_price',
                  width: 120,
                  align: 'right',
                  render: (price: number) => (
                    <Text style={{ fontSize: '12px', fontWeight: 400 }}>Nu. {price.toLocaleString()}</Text>
                  )
                },
                {
                  title: 'Received',
                  dataIndex: 'received_quantity',
                  key: 'received_quantity',
                  width: 100,
                  align: 'center',
                  render: (qty: number) => qty || 0
                },
                {
                  title: 'Still to receive',
                  key: 'still_to_receive',
                  width: 130,
                  align: 'center',
                  render: (_, record) => {
                    const left = Math.max(0, Number(record.quantity || 0) - Number(record.received_quantity || 0));
                    return <Text type={left > 0 ? 'warning' : 'secondary'}>{left}</Text>;
                  }
                }
              ]}
                />
              </>
            ) : null}

            <Divider />

            <Row justify="end">
              <Col>
                <Title level={4} style={{ fontSize: '12px', fontWeight: 400 }}>Total Amount: Nu. {(Number(viewingOrder.total_amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Title>
              </Col>
            </Row>

            {viewingOrder.notes && !viewingOrder.notes.startsWith('Created from Excel file:') && (
              <>
                <Divider />
                <p style={{ fontSize: '12px', fontWeight: 400 }}><span style={{ fontWeight: 400 }}>Notes:</span> {viewingOrder.notes}</p>
              </>
            )}

            <Divider />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Created by {viewingOrder.created_by_user?.first_name} {viewingOrder.created_by_user?.last_name} on {moment(viewingOrder.created_at).format('DD MMM YYYY, HH:mm')}
            </Text>
          </div>
        )}
      </ModernFormModal>
    </div>
  );
};

export default PurchaseOrders;
